import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import {
  PhoneIcon,
  PhoneXMarkIcon,
  MicrophoneIcon,
  XMarkIcon,
  ClockIcon,
  UserCircleIcon,
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  StarIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  ArrowPathIcon,
  MinusIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import {
  StopIcon as StopIconSolid
} from '@heroicons/react/24/solid';
import {
  PhoneIcon as PhoneIconSolid,
  MicrophoneIcon as MicrophoneIconSolid,
  StarIcon as StarSolid
} from '@heroicons/react/24/solid';
import { Button, Textarea, Switch, Badge } from 'components/ui';
import { toast } from 'sonner';
import { getIntegrationsByType, getOzonetelCallStatus } from 'services/integrationService';
import { CallServiceSelector } from 'components/shared/CallServiceSelector';
import axiosInstance from 'utils/axios';
import { useCall } from 'contexts/CallContext';

import { addLeadNote } from 'services/notesService';
import { formatDate, formatTime } from 'utils/dateUtils';

// Helper for formatting duration
const formatDurationHelper = (seconds) => {
  if (!seconds && seconds !== 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Independent Call History Item Component
const CallHistoryItem = ({ call }) => {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current = null;
    }
    setPlaying(false);
    setPaused(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    if (!playing || !audioRef.current) return;
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, x / width));

    const newTime = percentage * (audioRef.current.duration || 0);
    audioRef.current.currentTime = newTime;
    setProgress(percentage * 100);
    setCurrentTime(newTime);
  };

  const handlePlayPause = async (e) => {
    e.stopPropagation();

    // Resume if paused
    if (playing && paused && audioRef.current) {
      try {
        await audioRef.current.play();
        setPaused(false);
      } catch (err) {
        console.error("Resume error:", err);
        stopAudio();
      }
      return;
    }

    // Pause if playing
    if (playing && !paused && audioRef.current) {
      audioRef.current.pause();
      setPaused(true);
      return;
    }

    // Start new playback
    const callId = call.recordingSid || call.sid || call.id;
    if (!callId) {
      toast.error("Recording ID not available");
      return;
    }

    try {
      setLoading(true);

      // Fetch recording URL
      // We use the same recording fetching logic as before
      const response = await axiosInstance.get(`/api/Voice/recording/${callId}`);

      if (response.data && response.data.success && response.data.data && response.data.data.url) {
        const recordingSid = response.data.data.recordingId;
        const provider = call.provider;

        let audioUrl = null;

        if (provider === 'OZONETEL') {
          audioUrl = response.data.data.url;
        } else {
          // Twilio/Default Proxy
          const audioResponse = await axiosInstance.get('/api/Voice/play', {
            params: {
              recordingSid: recordingSid || callId,
              provider: provider || 'TWILIO'
            },
            responseType: 'blob'
          });
          const audioBlob = new Blob([audioResponse.data], { type: 'audio/mpeg' });
          audioUrl = URL.createObjectURL(audioBlob);
        }

        if (!audioUrl) throw new Error("Could not result audio URL");

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onloadedmetadata = () => {
          // setDuration(audio.duration); // Duration might be Infinity for streams, but usually finite here
          setDuration(audio.duration);
        };

        audio.ontimeupdate = () => {
          setCurrentTime(audio.currentTime);
          if (audio.duration) {
            setProgress((audio.currentTime / audio.duration) * 100);
          }
        };

        audio.onended = () => {
          // Don't stop entirely to preserve fetched recording
          setPaused(true);
          setCurrentTime(0);
          setProgress(0);
        };

        audio.onerror = (err) => {
          console.error("Audio error", err);
          toast.error("Failed to play audio");
          stopAudio();
        };

        await audio.play();
        setPlaying(true);
        setPaused(false);
      } else {
        toast.error("Recording not available");
      }
    } catch (err) {
      console.error("Playback error:", err);
      toast.error("Failed to load recording");
      stopAudio();
    } finally {
      setLoading(false);
    }
  };

  const getCallIcon = (type) => {
    return type === 'incoming' ?
      <PhoneArrowDownLeftIcon className="h-4 w-4" /> :
      <PhoneArrowUpRightIcon className="h-4 w-4" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 ring-green-600/20 dark:text-green-400 dark:bg-green-900/20';
      case 'failed':
        return 'text-red-600 bg-red-50 ring-red-600/20 dark:text-red-400 dark:bg-red-900/20';
      case 'missed':
        return 'text-yellow-600 bg-yellow-50 ring-yellow-600/20 dark:text-yellow-400 dark:bg-yellow-900/20';
      default:
        return 'text-gray-600 bg-gray-50 ring-gray-600/20 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  return (
    <div className="group relative rounded-lg border border-gray-100 bg-white p-3 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Icon */}
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${call.type === 'incoming'
            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
            : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
            }`}>
            {getCallIcon(call.type)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                {call.type === 'incoming' ? 'Incoming Call' : 'Outgoing Call'}
              </p>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${getStatusColor(call.status)} capitalize`}>
                {call.status}
              </span>
              {call.provider && (
                <Badge
                  variant="outline"
                  color="info"
                  className="text-[10px] px-1.5 py-0.5 border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300"
                >
                  {call.provider}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <CalendarDaysIcon className="h-3 w-3" />
                {formatDate(call.timestamp)}
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3" />
                {formatTime(call.timestamp)}
              </span>
              {call.duration > 0 && (
                <span className="flex items-center gap-1">
                  <PlayIcon className="h-3 w-3" />
                  {formatDurationHelper(call.duration)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Recording Playback Button (Mini) */}
        {(call.recordingSid && call.status === 'completed') && (
          <div className="flex items-center">
            {loading ? (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 dark:bg-gray-700">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
              </div>
            ) : (
              <Button
                size="xs"
                variant="ghost"
                isIcon
                onClick={handlePlayPause}
                className={`h-8 w-8 rounded-full transition-colors ${playing
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                  : 'bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300'
                  }`}
                title={playing && !paused ? "Pause" : "Play Recording"}
              >
                {playing && !paused ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Expanded Audio Player */}
      {playing && (
        <div className="mt-3 rounded-lg bg-gray-50 p-2.5 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono font-medium text-gray-500 dark:text-gray-400 min-w-[35px] text-right">
              {formatDurationHelper(currentTime)}
            </span>

            <div
              className="relative flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full cursor-pointer group py-0.5"
              onClick={handleSeek}
            >
              <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              <div
                className="absolute top-1/2 -translate-y-1/2 left-0 h-1 bg-primary-500 rounded-full transition-all duration-75 group-hover:bg-primary-600"
                style={{ width: `${progress}%` }}
              ></div>
            </div>

            <span className="text-[10px] font-mono font-medium text-gray-500 dark:text-gray-400 min-w-[35px]">
              {formatDurationHelper(duration || call.duration)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};


const CallModal = ({ isOpen, onClose, leadData: propLeadData, onCallComplete }) => {
  // Use global call context
  const {
    callState,
    callDuration,
    leadData: globalLeadData,
    isMuted,
    isRecording,
    callSid,
    recordingSid,
    errorMessage,
    setModalOpen,
    currentAudioDevice,
    availableAudioDevices,
    initiateCall,
    endCall,
    toggleMute,
    loadAudioDevices,
    formatDuration,
    connectDevice,
    setCallStatus,
    setLeadData,
    setOzonetelUcid,
    triggerOzonetelExtension,
    resetCallState,
    setActiveProvider
  } = useCall();

  // Local state for modal-specific functionality
  const [callNotes, setCallNotes] = useState('');
  const [isCallNoteImportant, setIsCallNoteImportant] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('call'); // 'call' or 'history'
  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [dialMode, setDialMode] = useState('Agent'); // 'Agent' or 'Phone' for Ozonetel

  // Voice Providers
  const [voiceProviders, setVoiceProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [showServiceSelector, setShowServiceSelector] = useState(false);

  const callButtonRef = useRef(null);

  // Use global lead data if available, otherwise use prop
  const leadData = globalLeadData || propLeadData;

  // Helper to get leadId from leadData (handles different property names)
  const getLeadId = (lead) => {
    return lead?.id || lead?.leadId || lead?.LeadId || null;
  };

  // Map API status to Timeline display status
  const mapStatus = (rawStatus) => {
    const status = String(rawStatus || 'unknown').toLowerCase();
    if (['completed', 'success'].includes(status)) return 'completed';
    if (['busy', 'failed', 'canceled', 'failure'].includes(status)) return 'failed';
    if (['no-answer', 'missed', 'did-not-pick-up'].includes(status)) return 'missed';
    return 'unknown';
  };

  // Fetch Voice Providers
  const fetchVoiceProviders = async () => {
    setLoadingProviders(true);
    try {
      const result = await getIntegrationsByType('CALL');

      if (result.success) {
        const data = result.data || [];
        setVoiceProviders(data);

        // Select default or first active one
        const defaultProvider = data.find(p => p.IsDefault) || data.find(p => p.ProductActive) || data[0];
        if (defaultProvider) {
          setSelectedProvider(defaultProvider);
        }
      } else {
        console.error('Failed to fetch voice providers:', result.error);
        toast.error('Could not load voice providers');
      }
    } catch (error) {
      console.error('Failed to fetch voice providers:', error);
      toast.error('Could not load voice providers');
    } finally {
      setLoadingProviders(false);
    }
  };

  // Fetch call history from API
  const fetchCallHistory = async (leadId) => {
    if (!leadId) {
      console.warn('⚠️ fetchCallHistory called with no leadId');
      return;
    }

    setLoadingHistory(true);
    try {
      const response = await axiosInstance.get('/api/Voice/GetCallsForLeadId', {
        params: {
          leadId: leadId,
          pageSize: 20,
          pageNumber: 1
        }
      });


      if (response.data) {
        // Data is now directly returned as an array or object from the optimized controller
        const callsData = JSON.parse(response.data.data) || response.data || [];

        // Transform API data to match our component format
        const transformedCalls = Array.isArray(callsData) ? callsData.map((call) => ({
          id: call.LogId || call.Sid, // Use LogId if available, fallback to Sid
          sid: call.MessageId || call.Sid, // MessageId is the CallSid
          recordingSid: call.MessageId || call.Sid || call.RecordingSid || (call.Recordings?.[0]?.Sid) || null,
          type: (call.DeliveryDirection === 1 || call.Direction === 'outbound-dial') ? 'outgoing' : 'incoming',
          status: mapStatus(call.StatusName || call.Status),
          duration: parseInt(call.Duration) || 0,
          timestamp: call.CreatedAt || call.StartTime,
          endTime: call.EndTime, // Might be null in log
          from: call.From || call.Recipient, // Recipient is usually 'To' for outbound
          to: call.To || call.Recipient,
          price: call.Price,
          priceUnit: call.PriceUnit,
          provider: call.ProviderCode // Show provider only if exists
        })) : [];

        setCallHistory(transformedCalls);
      }
    } catch (error) {
      console.error('❌ Error fetching call history:', error);
      toast.error('Failed to load call history');
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Reset modal state when opened and fetch call history
  useEffect(() => {
    if (isOpen) {
      setModalOpen(true); // Track that modal is open
      // setActiveTab('call');
      setCallNotes('');
      setIsCallNoteImportant(false);
      setIsSavingNote(false);

      if (callState === 'idle') {
        fetchVoiceProviders();
      }

    } else {
      setModalOpen(false); // Track that modal is closed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch call history when switching to history tab
  useEffect(() => {
    const extractedLeadId = getLeadId(leadData);
    if (activeTab === 'history' && extractedLeadId && callHistory.length === 0 && !loadingHistory) {
      fetchCallHistory(extractedLeadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);


  // Handle saving call-end notes
  const handleSaveCallEndNote = async () => {
    if (!callNotes.trim()) {
      // Allow closing without note
      handleClose();
      return;
    }

    const extractedLeadId = getLeadId(leadData);
    if (!extractedLeadId) {
      toast.error('Unable to save note: Lead ID not found');
      handleClose();
      return;
    }

    try {
      setIsSavingNote(true);

      // Use the same API as NotesTab
      const result = await addLeadNote(extractedLeadId, callNotes.trim(), isCallNoteImportant);

      if (result.success) {
        // Refresh call history after saving note
        await fetchCallHistory(extractedLeadId);
      }

      handleClose();
    } catch (err) {
      console.error('Error saving call-end note:', err);
      handleClose();
    } finally {
      setIsSavingNote(false);
    }
  };

  // Stop polling if call is manually ended
  useEffect(() => {
    // console.log("CallState Cleanup Effect:", callState);
    if (callState === 'ended' || callState === 'failed') {
      // console.log("Clearing Ozonetel polling due to callState:", callState);
      setOzonetelUcid(null);
    }
  }, [callState]);

  // Helper to initialize provider SDKs if needed
  const initializeProviderSDK = async (providerCode) => {
    // Standardize provider code check
    const code = providerCode?.toUpperCase();

    switch (code) {
      case 'TWILIO':
      case 'TWILIO_VOIP':
        try {
          // Initialize Twilio Device for browser-based calls
          await connectDevice();
          toast.loading('Device ready. Connecting...', { id: 'init-call' });
        } catch (err) {
          console.error("Device init failed:", err);
          throw new Error("Failed to initialize audio device");
        }
        break;
      case 'OZONETEL':
      case 'CLOUDCONNECT':
        // No client-side init needed
        break;
      default:
        break;
    }
  };

  const isWebCall = !selectedProvider || (selectedProvider.ProviderCode !== 'OZONETEL' && selectedProvider.ProviderCode !== 'CLOUDCONNECT');

  // Use global call functions but handle modal-specific logic
  const handleInitiateCall = async () => {
    if (!selectedProvider) {
      setShowServiceSelector(true);
      return;
    }

    // Initiate call via backend for ALL providers
    try {
      toast.loading('Initiating call...', { id: 'init-call' });

      // Initialize provider-specific SDK
      if (selectedProvider) {
        try {
          await initializeProviderSDK(selectedProvider.ProviderCode);
        } catch (error) {
          toast.error(error.message);
          return;
        }
      }

      // Ensure global context has the lead data for the widget
      if (leadData) {
        setLeadData(leadData);
      }

      // Set active provider for global context visibility logic
      if (selectedProvider) {
        setActiveProvider(selectedProvider.ProviderCode);
      }

      const response = await axiosInstance.post('/api/Voice/initiate-call', {
        LeadId: getLeadId(leadData),
        Provider: selectedProvider.ProviderCode,
        DialMode: dialMode // Pass 'Agent' or 'Phone' to backend
      });

      if (response.data.success) {
        const responseData = response.data.data || {};
        const providerCode = responseData.provider || selectedProvider.ProviderCode;

        if (providerCode === 'OZONETEL') {
          // For Ozonetel/CloudConnect (Click-to-Call)
          toast.success(responseData.message || response.data.message || 'Call initiated successfully', { duration: 5000, id: 'init-call' });

          // Trigger the Chrome Extension to open/dial
          const phoneNumber = leadData?.mobile || leadData?.phone;
          if (typeof triggerOzonetelExtension === 'function') {
            triggerOzonetelExtension(phoneNumber);
          }

          const ucid = responseData.callId || responseData.CallId || responseData.ucid || responseData.UCID;

          if (ucid) {
            // Set status FIRST to ensure cleanup effect doesn't perform premature cleanup
            setCallStatus('dialing');
            // Brief timeout to let state settle before setting UCID which triggers polling
            await new Promise(r => setTimeout(r, 100));
            setOzonetelUcid(ucid);
          } else {
            // Fallback if no UCID returned (old behavior)
            handleClose();
          }
        } else {
          // For Twilio (Browser SDK), trigger the global widget
          toast.success(response.data.message || 'Call initiated! connecting...', { id: 'init-call' });

          // Update state to dialing so global widget appears when modal closes
          setCallStatus('dialing');
          handleMinimize(); // Use minimize to preserve state/leadData for the Global Widget
        }
      } else {
        toast.error(response.data.message || 'Failed to initiate call', { id: 'init-call' });
      }
    } catch (error) {
      console.error('Call initiation error:', error);
      toast.error(error.response?.data?.message || 'Failed to initiate call', { id: 'init-call' });
    }
  };

  const handleClose = () => {
    // Always call onClose - global widget will handle call persistence
    if ((callState === 'ended' || callState === 'failed') && onCallComplete) {
      onCallComplete({
        status: callState,
        duration: callDuration,
        notes: callNotes
      });

      // Refresh call history after call ends
      const extractedLeadId = getLeadId(leadData);
      if (extractedLeadId) {
        fetchCallHistory(extractedLeadId);
      }
    }

    // Reset global call state when manually closing the modal (X button)
    resetCallState();

    // Mark modal as closed
    setModalOpen(false);
    onClose();
  };

  const handleMinimize = () => {
    setModalOpen(false);
    onClose();
  };


  const getStateColor = () => {
    switch (callState) {
      case 'dialing':
      case 'connecting':
        return 'bg-yellow-500';
      case 'connected':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'ended':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  const getStateText = () => {
    switch (callState) {
      case 'dialing':
        return 'Dialing...';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Connected';
      case 'failed':
        return 'Call Failed';
      case 'ended':
        return 'Call Ended';
      default:
        return 'Ready to Call';
    }
  };

  const isCallActive = ['dialing', 'connecting', 'connected'].includes(callState);

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[99998] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
          onClose={() => { }}
          initialFocus={callButtonRef}
        >
          <TransitionChild
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative flex w-full max-w-lg max-h-[100vh] origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">

              {/* Header */}
              <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
                <DialogTitle
                  as="h3"
                  className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
                >
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                  Voice Call
                </DialogTitle>

                {isCallActive ? (
                  <Button
                    onClick={handleMinimize}
                    variant="flat"
                    isIcon
                    className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    title="Minimize Call"
                  >
                    <MinusIcon className="size-4.5" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleClose}
                    variant="flat"
                    isIcon
                    className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                    title="Close"
                  >
                    <XMarkIcon className="size-4.5" />
                  </Button>
                )}
              </div>
              {/* Lead Information */}
              <div className="px-4 py-3 sm:px-5">
                <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-800 dark:text-blue-300">
                      {leadData?.name?.charAt(0)?.toUpperCase() || <UserCircleIcon className="h-6 w-6" />}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {leadData?.name || 'Unknown Contact'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {leadData?.mobile || leadData?.phone || 'No phone number'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="sticky top-0 z-20 bg-white dark:bg-dark-700 border-b border-gray-200 dark:border-gray-600">
                <div className="flex px-4 sm:px-5">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('call');
                    }}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${activeTab === 'call'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    <PhoneIcon className="h-4 w-4" />
                    Voice Call
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('history');
                    }}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${activeTab === 'history'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    <ClockIcon className="h-4 w-4" />
                    Call History
                  </button>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* Tab Content */}
                <div className="px-4 py-4 sm:px-5 min-h-[400px]">

                  {/* Call Tab Content */}
                  {activeTab === 'call' && (
                    <>
                      {/* Call Status */}
                      <div className="mb-6 text-center">
                        {/* Professional Call Icon */}
                        <div className="relative mb-8 flex justify-center">
                          {callState === 'idle' && (
                            <div className="relative">
                              {/* Subtle outer ring */}
                              <div className="absolute inset-0 -m-6 rounded-full bg-blue-50 dark:bg-blue-900/10"></div>
                              {/* Main icon container */}
                              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg dark:from-blue-600 dark:to-blue-700">
                                <PhoneIconSolid className="h-9 w-9 text-white" />
                              </div>
                            </div>
                          )}

                          {(callState === 'dialing' || callState === 'connecting') && (
                            <div className="relative">
                              <div className="absolute h-24 w-24 animate-ping rounded-full bg-yellow-400 opacity-30"></div>
                              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 shadow-lg">
                                <div className="absolute h-20 w-20 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
                                <PhoneIconSolid className="h-9 w-9 text-white" />
                              </div>
                            </div>
                          )}

                          {callState === 'connected' && (
                            <div className="relative">
                              {/* Subtle outer glow ring */}
                              <div className="absolute inset-0 -m-6 rounded-full bg-green-50 dark:bg-green-900/10"></div>
                              {/* Pulsing ring */}
                              <div className="absolute inset-0 -m-2 animate-pulse rounded-full bg-green-400 opacity-20"></div>
                              {/* Main icon container with even shadow */}
                              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-[0_4px_20px_rgba(34,197,94,0.4)]">
                                <PhoneIconSolid className="h-9 w-9 text-white" />
                              </div>
                            </div>
                          )}

                          {callState === 'failed' && (
                            <div className="relative">
                              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                                <PhoneIconSolid className="h-9 w-9 text-white" />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Professional Status Badge */}
                        <div className="flex justify-center mb-4">
                          <div className={`inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold transition-all duration-300 ${callState === 'connected'
                            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                            : callState === 'failed'
                              ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                              : callState === 'idle'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800'
                                : 'bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800'
                            }`}>
                            {callState === 'connected' && <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>}
                            {(callState === 'dialing' || callState === 'connecting') && <div className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse"></div>}
                            {getStateText()}
                          </div>
                        </div>

                        {callState === 'connected' && (
                          <div className="space-y-3">
                            <div className="rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 p-3 dark:from-green-900/10 dark:to-emerald-900/10">
                              <div className="flex items-center justify-center gap-2 text-xl font-mono font-bold text-green-700 dark:text-green-300">
                                <ClockIcon className="h-5 w-5" />
                                {formatDuration(callDuration)}
                              </div>
                              <p className="mt-1 text-xs text-green-600 dark:text-green-400">Call in progress</p>
                            </div>

                            {selectedProvider?.ProviderCode === 'OZONETEL' && (
                              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center dark:border-blue-800 dark:bg-blue-900/20">
                                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                  Note: To manage call (mute/hang up), please go to Ozonetel Dialer popup.
                                </span>
                              </div>
                            )}

                            {/* Recording Status */}
                            {isRecording && (
                              <div className="rounded-lg bg-gradient-to-r from-red-50 to-red-100 p-3 dark:from-red-900/10 dark:to-red-900/20 border border-red-200 dark:border-red-800">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="flex items-center gap-2">
                                    <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
                                    <span className="text-sm font-semibold text-red-700 dark:text-red-300">
                                      Recording Active
                                    </span>
                                  </div>
                                </div>
                                <p className="mt-1 text-xs text-red-600 dark:text-red-400 text-center">
                                  Call is being recorded
                                </p>
                              </div>
                            )}

                          </div>
                        )}

                        {(callState === 'dialing' || callState === 'connecting') && (
                          <div className="mt-2">
                            <div className="mx-auto h-3 w-56 overflow-hidden rounded-full bg-gray-200 shadow-inner dark:bg-gray-700">
                              <div
                                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-1000 ease-out"
                                style={{ width: callState === 'dialing' ? '30%' : '70%' }}
                              ></div>
                            </div>
                            <p className="mt-3 text-center text-sm font-medium text-gray-600 dark:text-gray-400">
                              {callState === 'dialing' ? 'Establishing connection...' : 'Almost connected...'}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Call Actions - Idle State with Professional Info */}
                      {/* Call Actions - Idle State with Professional Info */}
                      {callState === 'idle' && (
                        <div className="space-y-6">

                          {/* Provider Info & Selection */}
                          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                  <PhoneIcon className="h-5 w-5" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    Call via: {selectedProvider ? (selectedProvider.ProductName || selectedProvider.ProviderName) : "Select Provider"}
                                  </p>
                                  {selectedProvider && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {selectedProvider.ProviderName} {selectedProvider.IsDefault && "(Default)"}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outlined"
                                onClick={() => setShowServiceSelector(true)}
                              >
                                {selectedProvider ? "Change" : "Select"}
                              </Button>
                            </div>
                            
                            {/* Ozonetel Dial Mode Options */}
                            {selectedProvider?.ProviderCode === 'OZONETEL' && (
                              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 hidden">
                                <p className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">Choose Call Method:</p>
                                <div className="grid grid-cols-1 gap-3">
                                  <label className={`cursor-pointer rounded-lg border p-3 flex flex-col items-center gap-2 transition-colors ${dialMode === 'Agent' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:border-indigo-400 dark:text-indigo-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600 dark:border-gray-700 dark:hover:bg-gray-700/50 dark:text-gray-400'}`}>
                                    <input 
                                      type="radio" 
                                      name="dialMode" 
                                      value="Agent" 
                                      checked={dialMode === 'Agent'} 
                                      onChange={() => setDialMode('Agent')}
                                      className="sr-only" 
                                    />
                                    <ComputerDesktopIcon className="h-6 w-6" />
                                    <div className="text-center">
                                      <p className="text-sm font-medium">Browser Dial</p>
                                      <p className="text-[10px] opacity-80 mt-0.5">(Requires Extension)</p>
                                    </div>
                                  </label>
                                  {/* Phone Dial option removed as requested
                                  <label className={`cursor-pointer rounded-lg border p-3 flex flex-col items-center gap-2 transition-colors ${dialMode === 'Phone' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:border-indigo-400 dark:text-indigo-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600 dark:border-gray-700 dark:hover:bg-gray-700/50 dark:text-gray-400'}`}>
                                    <input 
                                      type="radio" 
                                      name="dialMode" 
                                      value="Phone" 
                                      checked={dialMode === 'Phone'} 
                                      onChange={() => setDialMode('Phone')}
                                      className="sr-only" 
                                    />
                                    <DevicePhoneMobileIcon className="h-6 w-6" />
                                    <div className="text-center">
                                      <p className="text-sm font-medium">Phone Dial</p>
                                      <p className="text-[10px] opacity-80 mt-0.5">(Rings Your Device)</p>
                                    </div>
                                  </label>
                                  */}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Contact Quick Info Card */}
                          <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm dark:border-gray-700 dark:from-gray-800 dark:to-gray-900">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Contact Number</p>
                                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                                  {leadData?.mobile || leadData?.phone || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Lead Source</p>
                                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                                  {leadData?.source || 'Direct'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Email</p>
                                <p className="mt-1 text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                  {leadData?.email || 'Not provided'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Stage</p>
                                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                                  {leadData?.stageName || leadData?.stage || 'New'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Inbound Call</p>
                                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                                  {callHistory.filter(call => call.type === 'incoming').length}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Outbound Call</p>
                                <p className="mt-1 font-semibold text-gray-900 dark:text-gray-100">
                                  {callHistory.filter(call => call.type === 'outgoing').length}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Call Action Button */}
                          <div className="text-center">
                            <Button
                              ref={callButtonRef}
                              onClick={handleInitiateCall}
                              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-green-600 px-8 py-3.5 text-base font-semibold text-white shadow-md hover:from-emerald-700 hover:to-green-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                            >
                              <PhoneIcon className="h-5 w-5" />
                              Initiate Call {selectedProvider?.ProviderCode === 'OZONETEL' && `(${dialMode})`}
                            </Button>
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                              Powered by {selectedProvider?.ProductName || selectedProvider?.ProviderName || 'Voice Provider'}
                            </p>
                          </div>

                          {/* Professional Tips */}
                          <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 dark:border-blue-900/30 dark:bg-blue-900/10">
                            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-300">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Before You Call
                            </h4>
                            <ul className="space-y-1.5 text-xs text-blue-800 dark:text-blue-300">
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>Review lead history and previous interactions</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>Ensure you&apos;re in a quiet environment</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>Have notepad ready for important points</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">•</span>
                                <span>Call recording available once connected</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {(callState === 'dialing' || callState === 'connecting') && (
                        <div className="text-center">
                          <Button
                            color="error"
                            size="lg"
                            onClick={() => setCallState('failed')}
                            className="min-w-[8rem] rounded-full px-6"
                          >
                            <PhoneXMarkIcon className="mr-2 h-5 w-5" />
                            Cancel
                          </Button>
                        </div>
                      )}

                      {callState === 'connected' && (
                        <div className="space-y-4">
                          {/* Call Controls */}
                          <div className="rounded-xl bg-gradient-to-b from-gray-50 to-gray-100 p-5 shadow-inner dark:from-gray-800 dark:to-gray-900">
                            <h4 className="mb-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                              Call Controls
                            </h4>

                            <div className="flex items-center justify-center gap-6">
                              {/* Mute Button - Only for Web Calls */}
                              {isWebCall && (
                                <div className="text-center">
                                  <button
                                    onClick={toggleMute}
                                    className={`relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-105 ${isMuted
                                      ? 'bg-red-500 text-white hover:bg-red-600 ring-4 ring-red-200 dark:ring-red-800'
                                      : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600'
                                      }`}
                                    title={isMuted ? 'Unmute' : 'Mute'}
                                  >
                                    {isMuted ? (
                                      <>
                                        <MicrophoneIconSolid className="h-7 w-7" />
                                        {/* Diagonal slash line */}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="h-10 w-0.5 bg-white rotate-45 rounded-full"></div>
                                        </div>
                                      </>
                                    ) : (
                                      <MicrophoneIcon className="h-7 w-7" />
                                    )}
                                  </button>
                                  <p className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {isMuted ? 'Muted' : 'Mute'}
                                  </p>
                                </div>
                              )}

                              {/* End Call Button - Only for Web Calls */}
                              {isWebCall && (
                                <div className="text-center">
                                  <Button
                                    color="error"
                                    size="lg"
                                    onClick={endCall}
                                    className="min-w-[10rem] rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-xl"
                                  >
                                    <PhoneXMarkIcon className="mr-2 h-5 w-5" />
                                    End Call
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {(callState === 'failed' || callState === 'ended') && (
                        <div className="space-y-4">
                          {callState === 'ended' && callDuration > 0 && (
                            <div className="rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-green-800 dark:text-green-300">
                                  Call Duration
                                </span>
                                <span className="text-sm font-mono text-green-600 dark:text-green-400">
                                  {formatDuration(callDuration)}
                                </span>
                              </div>
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Call Notes (Optional)
                            </label>
                            <Textarea
                              value={callNotes}
                              onChange={(e) => setCallNotes(e.target.value)}
                              placeholder="Add notes about this call..."
                              rows={4}
                              className="w-full"
                            />
                          </div>

                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-3">
                              <div className={`p-2 rounded-full transition-colors duration-200 ${isCallNoteImportant ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                                {isCallNoteImportant ? (
                                  <StarSolid className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                                ) : (
                                  <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                )}
                              </div>
                              <div>
                                <label
                                  htmlFor="isCallNoteImportant"
                                  onClick={() => setIsCallNoteImportant(!isCallNoteImportant)}
                                  className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                                >
                                  Mark as important
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {isCallNoteImportant ? "This note will be highlighted" : "This note will be regular"}
                                </p>
                              </div>
                            </div>
                            <Switch
                              id="isCallNoteImportant"
                              checked={isCallNoteImportant}
                              onCheckedChange={(event) => {
                                const checked = event.target ? event.target.checked : event;
                                setIsCallNoteImportant(checked);
                              }}
                              onChange={(event) => {
                                const checked = event.target ? event.target.checked : event;
                                setIsCallNoteImportant(checked);
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Bottom Actions */}
                      <div className="mt-6 space-x-3 text-end">
                        {callState === 'failed' && (
                          <Button
                            color="primary"
                            onClick={handleInitiateCall}
                            className="min-w-[7rem] rounded-full"
                          >
                            <PhoneIcon className="mr-2 h-4 w-4" />
                            Try Again
                          </Button>
                        )}
                        {(callState === 'failed' || callState === 'ended') && (
                          <Button
                            variant="outlined"
                            onClick={handleSaveCallEndNote}
                            disabled={isSavingNote}
                            className="min-w-[7rem] rounded-full"
                          >
                            {isSavingNote ? (
                              <div className="flex items-center">
                                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2"></div>
                                Saving...
                              </div>
                            ) : (
                              callNotes ? 'Save & Close' : 'Close'
                            )}
                          </Button>
                        )}
                      </div>
                    </>
                  )}

                  {/* Call History Tab Content */}
                  {activeTab === 'history' && (
                    <div className="space-y-4">
                      {loadingHistory ? (
                        <div className="flex flex-col items-center justify-center py-10 text-gray-500 text-sm">
                          <ArrowPathIcon className="h-6 w-6 animate-spin mb-2 text-gray-400" />
                          <p>Loading history...</p>
                        </div>
                      ) : callHistory.length > 0 ? (
                        <div className="space-y-3">
                          {callHistory.map((call) => (
                            <CallHistoryItem key={call.id || call.sid} call={call} />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-3">
                            <ClockIcon className="h-6 w-6 text-gray-400" />
                          </div>
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            No Call History
                          </h3>
                          <p className="mt-1 max-w-xs text-xs text-gray-500 dark:text-gray-400">
                            Previous calls with this lead will appear here.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      <CallServiceSelector
        isOpen={showServiceSelector}
        onClose={() => setShowServiceSelector(false)}
        onSelect={(service) => setSelectedProvider(service)}
        selectedService={selectedProvider}
      />
    </>
  );
};

export default CallModal;
